import { Link } from "react-router-dom";

import { Pill } from "../components/Badges";
import { Card, ErrorBox, Loading } from "../components/Page";
import { day, money, num } from "../lib/format";
import { useApi } from "../lib/useApi";

export interface GuideItem {
  description: string; item_code: string | null; qty: number | null; unit: string | null;
  unit_price: number | null; price: number | null; price_per: number | null; currency: string; quote: boolean;
  component: string | null; products: string[];
  from: { id: number; kind: string; number: string; date: string | null } | null; as_of: string | null;
}
interface Guide {
  party: { id: number; name: string; attn: string | null; email: string | null; phone: string | null };
  contact: { supplier: string; name: string | null; email: string | null; category: string | null } | null;
  currency: string;
  latest: { id: number; kind: string; number: string; date: string | null; ship_to: string | null; terms: string | null } | null;
  items: GuideItem[];
}

/** "$2,267.16 per 1,000": the price as the supplier wrote it. */
function priceAsWritten(it: GuideItem) {
  if (it.price == null) return "—";
  const per = it.price_per && it.price_per !== 1 ? ` per ${num(it.price_per)}` : "";
  return `${money(it.price, it.currency, it.price < 1 ? 4 : 2)}${per}`;
}

/** Who a purchase order to this supplier goes to, and what they have priced
 *  on the documents Zach sent: each item adds a line at its last price. */
export function PurchaseOrderGuide({ vendorId, onAdd }: { vendorId: string; onAdd: (it: GuideItem) => void }) {
  const { data, error, loading, reload } = useApi<Guide>(vendorId ? `/vendors/${vendorId}/order-guide` : null);
  if (!vendorId) return null;
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  // Never the previous supplier's items while the next one's load.
  if (!data || loading || String(data.party.id) !== vendorId) return <Loading />;
  const email = data.party.email ?? data.contact?.email?.split(/\s+/)[0] ?? null;
  return (
    <Card title={`Order from ${data.party.name}`}>
      <div className="po-guide-who">
        <span>{data.party.attn ?? data.contact?.name ?? "No contact on file"}</span>
        {email ? <a href={`mailto:${email}`}>{email}</a> : <span className="muted">no email on file</span>}
        {data.party.phone ? <span>{data.party.phone}</span> : null}
      </div>
      {data.latest ? (
        <p className="note">
          Their last document, {data.latest.kind.toLowerCase()} {data.latest.number} of {day(data.latest.date)}
          {data.latest.ship_to ? <>, shipped to {data.latest.ship_to}</> : null}
          {data.latest.terms ? <>. Terms: {data.latest.terms}</> : null}.{" "}
          <Link to={`/costs?view=invoices#doc-${data.latest.id}`}>See it</Link>
        </p>
      ) : null}
      {data.items.length ? (
        <div className="table-scroll">
          <table className="grid" data-testid="po-guide-items">
            <thead>
              <tr><th /><th>Item</th><th className="n">Last qty</th><th className="n">Price</th><th className="n">Each</th><th>From</th></tr>
            </thead>
            <tbody>
              {data.items.map((it, i) => (
                <tr key={i}>
                  {/* First, so it stays on screen on a phone while the table scrolls sideways. */}
                  <td><button className="btn btn-sm" onClick={() => onAdd(it)} aria-label={`Add ${it.description}`}>Add</button></td>
                  <td>
                    {it.description}
                    {it.item_code ? <div className="muted small">their code <code>{it.item_code}</code></div> : null}
                  </td>
                  <td className="n">{num(it.qty, 3)}{it.quote ? <div className="muted small">or more</div> : null}</td>
                  <td className="n">{priceAsWritten(it)}</td>
                  <td className="n">{it.unit_price == null ? "—" : money(it.unit_price, it.currency, 5)}</td>
                  <td>
                    {it.from ? <>{it.from.kind} {it.from.number}<div className="muted small">{day(it.from.date)}</div></> : <>price list<div className="muted small">{day(it.as_of)}</div></>}
                    {it.quote ? <Pill tone="neutral">estimate</Pill> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="note muted">Nothing priced from {data.party.name} yet. Their invoices and price lists upload on the <Link to="/sources">Integrations</Link> page.</p>
      )}
    </Card>
  );
}
